import {
  escapeHtmlText,
  readLockState,
  removeElementAttributeBySelector,
  setElementAttribute,
  USER_LOCK_ATTR,
} from '@/entities/section-picker';
import type { ElementLockState, SectionPickerClickPayload } from '@/entities/section-picker';

export const EMPTY_LOCK_STATE: ElementLockState = {
  locked: false,
  canUnlock: false,
  lockedByTemplate: false,
};

/**
 * The content string the edit dialog was opened with — the same expression that seeds its textbox.
 * Shared so the "did the user actually change the copy?" comparison in {@link handleCopyEditConfirm}
 * cannot drift from what was shown.
 */
export function openedContentOf(element: SectionPickerClickPayload): string {
  return element.hasChildElements ? element.innerHTML : element.fullTextContent;
}

/**
 * Inverse of `openedContentOf` — what to write back for what it handed out. Lives next to it on
 * purpose: an element with no markup children is opened as DECODED text, so returning it raw
 * promotes it (`&amp;` collapses to a bare `&`, and text reading `<hi>` becomes a real unclosed
 * element). The two functions have to move together, and keeping them apart is how they drifted.
 */
export function savedContentOf(element: SectionPickerClickPayload, edited: string): string {
  if (!element.hasChildElements) return escapeHtmlText(edited);
  // The markup branch carries the LIVE preview DOM, which the picker runtime has stamped with
  // `data-updatable-tagged` for its outline CSS. Writing that back bakes our own bookkeeping into
  // the published email — and, on an element whose real slug is untagged later, leaves the marker as
  // the only `data-updatable-*` present, which the backend would then report as a slot.
  return edited.replace(/\s*data-updatable-tagged(?:="[^"]*")?/gi, '');
}

/**
 * Apply the user's lock choice to the clicked element, returning the new HTML or null when the write
 * did not take effect. Applied BEFORE any content change so the two land as one step: a lock left
 * behind by a content write that then failed would silently freeze copy the user never protected.
 *
 * A no-op when the element already matches the choice — notably when the lock is inherited from an
 * ancestor or owned by the template, where writing a marker onto the child would add clutter without
 * changing anything.
 *
 * The unlock path is verified rather than assumed: it rewrites raw HTML, so hand-authored markup can
 * defeat it, and reporting "unlocked" while the marker survives would leave the agent refusing to
 * touch content the user believes they freed.
 */
export function applyLockChoice(
  html: string,
  selector: string,
  locked: boolean,
  current: ElementLockState,
): string | null {
  if (locked === current.locked) return html;
  if (locked) {
    const result = setElementAttribute(html, selector, USER_LOCK_ATTR, '1');
    return result.success ? result.html : null;
  }
  const unlocked = removeElementAttributeBySelector(html, selector, USER_LOCK_ATTR);
  // A marker known to be on THIS element must have changed the string; an unchanged result means the
  // selector resolved to nothing (or the strip missed), which must not be reported as an unlock.
  if (current.canUnlock && unlocked === html) return null;
  // `canUnlock` is true only while a user marker sits on this exact element, so its absence is the
  // proof the strip landed.
  return readLockState(unlocked, selector).canUnlock ? null : unlocked;
}
